import axios from 'axios';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import loading from '../images/loading.svg';
import quality from '../images/quality.webp';
import warranty from '../images/warranty.webp';
import cert from '../images/cert.webp';
import ReviewForm from './ReviewForm';
import userIcon from '../images/user.png'
import config from '../config'
import { useTranslation } from 'react-i18next'
import { formatPrice } from '../utils/formatPrice'
import { useCountry } from '../context/CountryContext'
import { isAdmin as getIsAdmin } from '../utils/auth'

export default function ProductDetails({cart, setCart}) {
    const { t, i18n } = useTranslation();
    const { country, currency } = useCountry();
    const {id} = useParams();
    const [product, setProduct] = useState(null);
    const isAdmin = getIsAdmin();
    const [gallery, setGallery] = useState([]);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [installation, setInstallation] = useState(false);
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [showImage, setShowImage] = useState(false);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);
   const navigate = useNavigate();
    useEffect(() => {
        axios.get(`${config.API_URL}/api/product/get/${id}`, {params: {lang: i18n.language, country}})
            .then(response => {
                setProduct(response.data);
                const images = [response.data.image, ...response.data.gallery.map(img => img.imageUrl)];
                setGallery(images);
            })
            .catch(error => {
                console.error(error);
            });
    }, [id, i18n.language, country]);
    const averageRate = useMemo(() => {
        if (!product || !product.reviews || product.reviews.length === 0) return 0;
        const total = product.reviews.reduce((sum, review) => sum + review.rate, 0);
        return total / product.reviews.length;
    }, [product]);

    // Refs for swipe/drag support must be declared before any early returns
    const dragStartXRef = useRef(0);
    const isDraggingRef = useRef(false);
    const hasMovedRef = useRef(false);
    const thumbRefs = useRef([]);
    const wheelThrottleRef = useRef(false);
    const lightboxDragStartXRef = useRef(0);
    const lightboxDraggingRef = useRef(false);

    useEffect(() => {
        const thumb = thumbRefs.current[currentSlide];
        if (thumb) {
            thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
    }, [currentSlide]);

    useEffect(() => {
        if (!showImage) return;
        function handleKeyDown(e) {
            if (e.key === 'ArrowRight') lightboxNext();
            else if (e.key === 'ArrowLeft') lightboxPrev();
            else if (e.key === 'Escape') setShowImage(false);
        }
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showImage, lightboxImages.length]);

    if (!product) {
        return <div className='loading'><img src={loading} alt='none gif'></img></div>
    }

    function handleChangeCheckbox(event) {
        setInstallation(event.target.checked);
    };

    function deleteReview(reviewId) {
        axios.delete(`${config.API_URL}/api/review/delete/${reviewId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem("access_tocken")}` }
        }).then(() => {
            setProduct(prev => ({
                ...prev,
                reviews: prev.reviews.filter(r => r.id !== reviewId)
            }));
        }).catch(console.error);
    }

    function addToCart(id) {
        const existingProduct = cart.find(item => item.product.id === id);
        if (existingProduct) {
            setCart(cart.map(item =>
                item.product.id === id ? {...item, quantity: item.quantity + 1, installation: installation} : item
            ));
        } else {
            setCart([...cart, {product: product, quantity: 1, installation: installation}]);
        }
        navigate("/cart")
    }

    const formattedDemensions = product.demensions ? product.demensions.split("\n").map((line, index) => (
        <span key={index}>{line}<br/></span>
    )) : [];

    const handleTouchStart = (e) => {
        if (!gallery.length) return;
        dragStartXRef.current = e.touches[0].clientX;
        isDraggingRef.current = true;
        hasMovedRef.current = false;
    };

    const handleTouchMove = (e) => {
        if (!isDraggingRef.current) return;
        const deltaX = e.touches[0].clientX - dragStartXRef.current;
        if (Math.abs(deltaX) > 10) {
            hasMovedRef.current = true;
        }
    };

    const handleTouchEnd = (e) => {
        if (!isDraggingRef.current) return;
        const endX = (e.changedTouches && e.changedTouches[0]?.clientX) || dragStartXRef.current;
        const deltaX = endX - dragStartXRef.current;
        isDraggingRef.current = false;
        if (Math.abs(deltaX) > 50) {
            if (deltaX < 0) {
                nextSlide();
            } else {
                prevSlide();
            }
        }
    };

    const handleMouseDown = (e) => {
        dragStartXRef.current = e.clientX;
        isDraggingRef.current = true;
        hasMovedRef.current = false;
    };

    const handleMouseMove = (e) => {
        if (!isDraggingRef.current) return;
        const deltaX = e.clientX - dragStartXRef.current;
        if (Math.abs(deltaX) > 10) {
            hasMovedRef.current = true;
        }
    };

    const endMouseDrag = (e) => {
        if (!isDraggingRef.current) return;
        const deltaX = e.clientX - dragStartXRef.current;
        isDraggingRef.current = false;
        if (Math.abs(deltaX) > 50) {
            if (deltaX < 0) {
                nextSlide();
            } else {
                prevSlide();
            }
        }
    };

    const handleWheel = (e) => {
        if (wheelThrottleRef.current) return;
        if (Math.abs(e.deltaY) < 5 && Math.abs(e.deltaX) < 5) return;
        wheelThrottleRef.current = true;
        setTimeout(() => { wheelThrottleRef.current = false; }, 300);
        if (e.deltaY > 0 || e.deltaX > 0) {
            nextSlide();
        } else {
            prevSlide();
        }
    };

    const nextSlide = () => {
        setCurrentSlide(prev => (prev + 1) % gallery.length);
    };

    const prevSlide = () => {
        setCurrentSlide(prev => (prev - 1 + gallery.length) % gallery.length);
    };

    function openLightbox(images, index) {
        setLightboxImages(images);
        setLightboxIndex(index);
        setShowImage(true);
    }

    function lightboxNext() {
        setLightboxIndex(prev => (prev + 1) % lightboxImages.length);
    }

    function lightboxPrev() {
        setLightboxIndex(prev => (prev - 1 + lightboxImages.length) % lightboxImages.length);
    }

    const handleLightboxTouchStart = (e) => {
        lightboxDragStartXRef.current = e.touches[0].clientX;
        lightboxDraggingRef.current = true;
    };

    const handleLightboxTouchEnd = (e) => {
        if (!lightboxDraggingRef.current) return;
        lightboxDraggingRef.current = false;
        const endX = (e.changedTouches && e.changedTouches[0]?.clientX) || lightboxDragStartXRef.current;
        const deltaX = endX - lightboxDragStartXRef.current;
        if (Math.abs(deltaX) > 50) {
            if (deltaX < 0) lightboxNext();
            else lightboxPrev();
        }
    };

    return (
        <>             {showImage && (
            <div className='imageContainer' onClick={() => setShowImage(false)}>
                <button className='closeButton' onClick={() => setShowImage(false)}>✕</button>
                {lightboxImages.length > 1 && (
                    <button className='lightbox-prev' onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}>&#10094;</button>
                )}
                <img
                    src={lightboxImages[lightboxIndex]}
                    alt="Full View"
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={handleLightboxTouchStart}
                    onTouchEnd={handleLightboxTouchEnd}
                />
                {lightboxImages.length > 1 && (
                    <button className='lightbox-next' onClick={(e) => { e.stopPropagation(); lightboxNext(); }}>&#10095;</button>
                )}
            </div>
        )}
            <div className='container'>
                <div className='productDetails'>
                    <div className='view'>
                        <div className='img-area'
                             onTouchStart={handleTouchStart}
                             onTouchMove={handleTouchMove}
                             onTouchEnd={handleTouchEnd}
                             onMouseDown={handleMouseDown}
                             onMouseMove={handleMouseMove}
                             onMouseUp={endMouseDrag}
                             onMouseLeave={endMouseDrag}
                             onWheel={handleWheel}
                        >
                            <button className="prev" onClick={prevSlide}>&#10094;</button>
                            <img onClick={() => openLightbox(gallery, currentSlide)} src={gallery[currentSlide]} alt="Product"/>
                            <button className="next" onClick={nextSlide}>&#10095;</button>
                        </div>
                        <div className="thumbnail-gallery">
                            <div className="thumbs-container">
                                {gallery.map((img, index) => (
                                    <img key={index}
                                         ref={el => { thumbRefs.current[index] = el; }}
                                         src={img}
                                         alt="Thumbnail"
                                         className={currentSlide === index ? "active-thumbnail" : ""}
                                         onClick={() => setCurrentSlide(index)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className='information'>
                        <h1>{product.name}</h1>
                        {product.soldOut && (
                            <span style={{
                                display: 'inline-block', background: '#c0392b', color: '#fff',
                                padding: '4px 12px', fontSize: '14px', fontWeight: 'bold',
                                borderRadius: '4px', marginBottom: '10px'
                            }}>Sold out</span>
                        )}
                        <div className='price'>
                            <p>{formatPrice(product.price, currency)}</p>
                            <s>{formatPrice(product.lastPrice, currency)}</s>
                        </div>
                        <div className='installation'>
                            <input type='checkbox' checked={installation} onChange={handleChangeCheckbox}></input>
                            <p>{t('product.installation')} + {formatPrice(product.installationPrice, currency)}</p>
                        </div>
                        <button disabled={product.soldOut} onClick={() => addToCart(product.id, installation)}>{product.soldOut ? 'Sold out' : t('product.addToCart')}</button>
                        <div className="description">
                            <p>{product.description}</p>
                        </div>
                    </div>

                </div>
<div className='composition'>
                    <img src={product.compositionImage} alt="Composition"/>
                    <ul>
                        <p>{t('product.setComposition')}</p>
                        {product.composition && product.composition.split("\n").map((item, index) => (
                            <li key={index}>{item}</li>
                        ))}
                    </ul>
                </div>
{product.movie && (
        <video autoPlay muted loop playsInline>
        <source src={product.movie} type="video/mp4" />
        </video>
)
}

                <div className='demensions'>
                    <p>{t('product.assembledDimensions')}</p>
                    <p>{formattedDemensions}</p>
                </div>

                 <p style={{textAlign: 'center', fontSize: '25px', marginTop: '50px'}}>{t('product.whyTitle')}</p>
                <ul className='whyBlock'>
                    <li>
                        <img src={quality}/>
                        <h1>{t('product.why1Title')}</h1>
                        <p>{t('product.why1Text')}</p>
                    </li>
                    <li>
                        <img src={cert}/>
                        <h1>{t('product.why2Title')}</h1>
                        <p>{t('product.why2Text')}</p>
                    </li>
                    <li>
                        <img src={warranty}/>
                        <h1>{t('product.why3Title')}</h1>
                        <p>{t('product.why3Text')}</p>
                    </li>
                </ul>

                <div className='reviewDetails'>
                    <p>{t('product.customerReviews')}</p>

                    {[1, 2, 3, 4, 5].map((star) => (
                        <span
                            key={star}
                            style={{
                                color: averageRate >= star ? 'gold' : 'gray',
                                fontSize: '24px',
                            }}
                        >
                              ★
                            </span>
                    ))}
                    <span> /</span>
                    <span style={{color: 'gold', fontSize: '24px'}}> ★★★★★</span>
                    <p>{averageRate.toFixed(2)} / 5.00</p>
                    <p>{t('product.basedOn', { count: product.reviews.length })}</p>
                    <button onClick={() => setShowReviewForm(!showReviewForm)}>{t('product.writeReview')}</button>
                </div>
                {showReviewForm &&
                    <ReviewForm setShowReviewForm={setShowReviewForm} product={product}/>}
                <div className='reviews'>
                    {product.reviews.map(item => (
                        <div className='comment' key={item.id}>
                            <div className='userInfo'>
                                <img src={userIcon}/>
                                <p>{item.fullName}</p>
                                {isAdmin && (
                                    <span
                                        onClick={() => deleteReview(item.id)}
                                        style={{marginLeft: 'auto', cursor: 'pointer', color: 'red', fontWeight: 'bold'}}
                                    >X</span>
                                )}
                            </div>
                            {[1, 2, 3, 4, 5].map((star) => (
                                <span
                                    key={star}
                                    style={{
                                        color: item.rate >= star ? 'gold' : 'gray',
                                        fontSize: '24px',
                                    }}
                                >
                              ★
                            </span>
                            ))}
                            <p>{item.text}</p>
                            <ul>{item.reviewGallery.map((reviewImage, reviewImageIndex) => (

                                <li key={reviewImage.id}>
                                    <img onClick={() => openLightbox(item.reviewGallery.map(g => g.imageUrl), reviewImageIndex)} src={reviewImage.imageUrl}/>
                                </li>
                            ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}
